import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50" }
const _hoisted_2 = { class: "w-full md:px-6 py-8" }
const _hoisted_3 = {
  key: 0,
  class: "flex justify-center items-center py-12"
}
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "hidden md:grid grid-cols-5 gap-4 mb-8" }
const _hoisted_6 = { class: "relative md:hidden mb-8" }
const _hoisted_7 = { class: "space-y-6" }

import { ref, reactive, onMounted, watch, computed, nextTick } from 'vue'
import type { GeneralInformationFormData, ParsedVendeurData, VendeurApiData, ImageUrls, UploadedImages } from '@/types'
import { 
  PageHeader, 
  AccountSettingsSection,
  AddressesSection,
  LeavesSection,
  ImagesSection,
  DescriptionSection,
  ConfirmModal
} from '@/components'
import { 
  getVendeurGeneralInfo, 
  updateVendeurGeneralInfo,
  saveVendeurAccountSettings,
  saveVendeurAddresses,
  saveVendeurLeaves,
  saveVendeurImages,
  saveVendeurDescriptions,
  deleteVendeurImage
} from '@/api/vendeur'
import { useNotification } from '@/composables/useNotification'
import { getSellerImageUrl } from '@/config/api'
import {useAuthStore} from "@/stores/auth";
import {useRouter} from "vue-router";

// État réactif

export default /*@__PURE__*/_defineComponent({
  __name: 'GeneralInformationView',
  setup(__props) {

const isSaving = ref(false)
const isLoading = ref(false)
const activeSection = ref('account') 
const formData = ref<GeneralInformationFormData | null>(null)
const imagesSectionRef = ref() 
const mobileNavRef = ref<HTMLElement | null>(null)
const uploadedImages = ref<UploadedImages>({ logo: null, banniere: null, square: null })
const imageUrls = ref<ImageUrls>({})
const authStore = useAuthStore()
const router = useRouter()
const showValidationModal = ref(false)
// Notifications
const { success, error } = useNotification()
const hasValidationErrors = ref(false)
const hasAccountErrors = ref(false)
const hasAddressErrors = ref(false)
// Message d'erreur détaillé pour la section Paramètres du compte
const accountError = ref('')

// Suivi des modifications non sauvegardées
const originalSnapshot = ref<string>('')
const serializeState = () => {
  return JSON.stringify({
    formData: formData.value,
    images: {
      logo: uploadedImages.value.logo ? (uploadedImages.value.logo as any).name || true : null,
      banniere: uploadedImages.value.banniere ? (uploadedImages.value.banniere as any).name || true : null,
      square: uploadedImages.value.square ? (uploadedImages.value.square as any).name || true : null,
    }
  })
}
const takeSnapshot = () => { originalSnapshot.value = serializeState() }
const hasUnsavedChanges = computed(() => {
  if (!formData.value) return false
  return serializeState() !== originalSnapshot.value
})

// Méthodes
const scrollNav = (direction: 'left' | 'right') => {
  if (!mobileNavRef.value) return
  const scrollAmount = 130
  mobileNavRef.value.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: 'smooth'
  })
}

const handleImagesChanged = (images: UploadedImages) => {
  uploadedImages.value = images
}

const handleDeleteImage = async (type: 'banniere' | 'square' | 'logo-boutique') => {
  try {
    await deleteVendeurImage(type)
    // Mettre à jour les URLs locales pour masquer l'image supprimée
    imageUrls.value = { ...imageUrls.value, [type]: '' }
    success('Image supprimée avec succès')
  } catch (e) {
    error('Erreur lors de la suppression de l\'image')
  }
}

const handleValidationChanged = (hasErrors: boolean) => {
  hasAccountErrors.value = hasErrors
  updateGlobalValidation()
}

const handleAddressValidationChanged = (hasErrors: boolean) => {
  hasAddressErrors.value = hasErrors
  updateGlobalValidation()
}

const updateGlobalValidation = () => {
  hasValidationErrors.value = hasAccountErrors.value || hasAddressErrors.value
}

// Nettoyer le message d'erreur quand l'utilisateur modifie les champs
watch(() => formData.value, () => {
  accountError.value = ''
}, { deep: true })
watch(() => uploadedImages.value, () => {
  // images modifiées => toujours considérer comme changements
}, { deep: true })

const saveForm = async () => {
  //éviter le double envoi
  if (isSaving.value || !formData.value) {
    return
  }
  // Bloquer si des champs requis manquent et afficher un pop-up
  if (hasValidationErrors.value) {
    showValidationModal.value = true
    return
  }
  
  isSaving.value = true
  
  try {
    const result = await updateVendeurGeneralInfo(formData.value, uploadedImages.value)
    
    if (result) {
      success('Vos informations ont été sauvegardées avec succès !')
      // Recharger les données depuis l'API pour s'assurer du retour en base + rafraîchir les visuels
      await loadVendeurData()
    } else {
      error('Une erreur est survenue lors de la sauvegarde')
    }
  } catch (err) {
    error('Une erreur est survenue lors de la sauvegarde')
  } finally {
    isSaving.value = false
  }
}

// Sauvegardes par section
const saveAccount = async () => {
  if (isSaving.value || !formData.value) return
  if (hasAccountErrors.value) { showValidationModal.value = true; return }
  isSaving.value = true
  try {
    const ok = await saveVendeurAccountSettings(formData.value)
    if (ok) {
      success('Paramètres du compte sauvegardés ✔')
      await loadVendeurData()
      accountError.value = ''
    }
  } catch (e) {
    const msg = extractErrorMessage(e)
    accountError.value = msg
    error(msg)
  } finally {
    isSaving.value = false
  }
}

const saveAddresses = async () => {
  if (isSaving.value || !formData.value) return
  if (hasAddressErrors.value) { showValidationModal.value = true; return }
  isSaving.value = true
  try {
    const ok = await saveVendeurAddresses(formData.value)
    if (ok) {
      success('Adresses sauvegardées ✔')
      await loadVendeurData()
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

const saveLeaves = async () => {
  if (isSaving.value || !formData.value) return
  isSaving.value = true
  try {
    const ok = await saveVendeurLeaves(formData.value)
    if (ok) {
      success('Congés sauvegardés ✔')
      await loadVendeurData()
    } else {
      error('Échec de la sauvegarde des congés')
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

// Ajout d'un congé : on reçoit directement les données mises à jour et on
// sauvegarde puis on rafraîchit. Si une sauvegarde est déjà en cours, on
// attend qu'elle se termine au lieu d'ignorer silencieusement la demande
// (c'est ce qui faisait que l'ajout n'était parfois pas pris en compte).
const saveLeavesWith = async (data: GeneralInformationFormData) => {
  formData.value = data

  if (isSaving.value) {
    await new Promise<void>((resolve) => {
      const stop = watch(isSaving, (v) => {
        if (!v) {
          stop()
          resolve()
        }
      })
    })
  }

  isSaving.value = true
  try {
    const ok = await saveVendeurLeaves(formData.value)
    if (ok) {
      success('Congé ajouté ✔')
      await loadVendeurData()
    } else {
      error("Échec de l'ajout du congé")
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

const saveImages = async () => {
  if (isSaving.value) return
  isSaving.value = true
  try {
    const ok = await saveVendeurImages(uploadedImages.value)
    if (ok) {
      success('Images sauvegardées ✔')
      await loadVendeurData()
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

const saveDescriptions = async () => {
  if (isSaving.value || !formData.value) return
  isSaving.value = true
  try {
    const ok = await saveVendeurDescriptions(formData.value)
    if (ok) {
      success('Descriptions sauvegardées ✔')
      await loadVendeurData()
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

// Normaliser la valeur du centre pour correspondre aux options du select
const normalizeCenterValue = (value: string): string => {
  if (!value) return ''
  const normalized = value.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
  const knownValues = ['aeroville', 'cifa', 'fashion_center', 'lem888', 'silk_road_paris']
  if (knownValues.includes(normalized)) return normalized
  // Essayer une correspondance partielle
  const match = knownValues.find(k => k === value.toLowerCase() || value.toLowerCase().includes(k.replace(/_/g, ' ')) || k.replace(/_/g, ' ').includes(value.toLowerCase()))
  return match || value
}

// Fonction pour charger les données depuis l'API
const loadVendeurData = async () => {
  isLoading.value = true
  try {
    const data = await getVendeurGeneralInfo()
    
    // Parser les données JSON stockées dans vendeurData.data
    let parsedData: ParsedVendeurData = {}
    const apiData = data as unknown as VendeurApiData

    if (apiData.vendeurData?.data) {
      try {
        parsedData = JSON.parse(apiData.vendeurData.data) as ParsedVendeurData
      } catch (error) {
        // Erreur silencieuse lors du parsing
      }
    }
    
    // Récupérer les adresses par type
    const storeAddress = apiData?.adresses?.find(addr => addr.type === 2) // type=2 = magasin
    const billingAddress = apiData?.adresses?.find(addr => addr.type === 3) // type=3 = facturation
    

    
    // Trier les éventuels doublons historiques afin de relire la ligne la plus
    // récente, comme le site acheteur.
    const languageRows = [...(apiData?.vendeursLangues || [])].sort(
      (left, right) => Number(left.id_vendeur_langue) - Number(right.id_vendeur_langue),
    )
    const baselineData: Record<string, string> = languageRows.reduce((acc, lang) => {
      acc[lang.id_langue.toLowerCase()] = lang.baseline || ''
      return acc
    }, {} as Record<string, string>) || {}
    
    const descriptionData: Record<string, string> = languageRows.reduce((acc, lang) => {
      acc[lang.id_langue.toLowerCase()] = lang.description || ''
      return acc
    }, {} as Record<string, string>) || {}
    
    // Récupérer les congés (actifs et supprimés)
    const leavesData: Array<{ id?: number; startDate: string; endDate: string; deleted?: boolean }> = apiData?.vendeursConges?.map(conge => ({
      id: conge.id_vendeur_conge,
      startDate: conge.debut_conge,
      endDate: conge.fin_conge,
      deleted: conge.supprimer === 1
    })) || []
    
    // Générer les URLs des images
    const userId = (apiData as any)?.id_vendeur
    if (userId) {
      const ts = Date.now()
      imageUrls.value = {
        logo    : getSellerImageUrl(`/uploads/sellers/${userId}/logo-boutique.jpg?t=${ts}`),
        banniere: getSellerImageUrl(`/uploads/sellers/${userId}/banniere.jpg?t=${ts}`),
        square  : getSellerImageUrl(`/uploads/sellers/${userId}/square.jpg?t=${ts}`)
      }
    }

    // formData directement avec les données du vendeur
    formData.value = reactive<GeneralInformationFormData>({
      firstName: apiData?.prenomContact || '',
      lastName: apiData?.nomContact || '',
      email: apiData?.email || '',
      emailSecondary: apiData?.email2 || '',
      minOrder: apiData?.commandeMini || 0,
      website: apiData?.siteWeb || '',
      socialMedia: {
        facebook: (typeof parsedData['social-url'] === 'object' && parsedData['social-url']?.facebook) || '',
        twitter: (typeof parsedData['social-url'] === 'object' && parsedData['social-url']?.twitter) || '',
        instagram: (typeof parsedData['social-url'] === 'object' && parsedData['social-url']?.instagram) || '',
        tiktok: (typeof parsedData['social-url'] === 'object' && parsedData['social-url']?.tiktok) || ''
      },
      baseline: {
        fr: baselineData?.fr || '',
        en: baselineData?.en || '',
        es: baselineData?.es || '',
        it: baselineData?.it || ''
      },
      banking: {
        iban: apiData?.vendeurData?.iban || (Array.isArray(parsedData.iban) ? parsedData.iban.join('') : (parsedData.iban || '')),
        bic: parsedData.bic || '',
        accountTitle: apiData?.vendeurData?.accountTitle || ''
      },
      security: {
        changePassword: false,
        newPassword: '',
        confirmPassword: ''
      },
      shop: {
        status: apiData?.statut === 1 ? 'online' : 'offline',
        catalogVisibility: apiData?.mea_month_delay?.toString() || '0',
        smsNotification: apiData?.vendeurData?.groupage === 1,
        showMainColor: apiData?.vendeurData?.displayColormain === 1
      },
      storeAddress: {
        storeName: storeAddress?.NomContact || apiData?.nomBoutique || '',
        number: storeAddress?.numero || '',
        street: storeAddress?.rue ? `${storeAddress?.numero || ''} ${storeAddress?.rue}`.trim() : storeAddress?.adresse || '',
        complement: storeAddress?.complement || '',
        center: normalizeCenterValue(storeAddress?.centre || ''),
        building: storeAddress?.batiment || '',
        lot: storeAddress?.immeuble || '',
        city: storeAddress?.ville || '',
        postalCode: storeAddress?.codePostal || '',
        phone: storeAddress?.telephone || '',
        mobile: storeAddress?.mobile || ''
      },
      billingAddress: {
        company: billingAddress?.Societe || apiData?.nomSociete || '',
        number: billingAddress?.numero || '',
        street: billingAddress?.rue ? `${billingAddress?.numero || ''} ${billingAddress?.rue}`.trim() : billingAddress?.adresse || '',
        city: billingAddress?.ville || '',
        postalCode: billingAddress?.codePostal || '',
        phone: billingAddress?.telephone || '',
        siret: apiData?.siret || '',
        tva: apiData?.tva || '',
        eori: apiData?.vendeurData?.eori || '',
        sameAsStore: false
      },
      leaves: leavesData,
      description: {
        fr: descriptionData?.fr || '',
        en: descriptionData?.en || '',
        es: descriptionData?.es || '',
        it: descriptionData?.it || ''
      }
    })
    // snapshot après chargement (attendre le rendu pour éviter les faux positifs)
    await nextTick()
    takeSnapshot()
    
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue'
    error(`Erreur lors du chargement des données: ${errorMessage}`)
  } finally {
    isLoading.value = false
  }
}

// Gestion changement de section avec avertissement de sauvegarde
const saveCurrentSection = async () => {
  if (!formData.value) return
  switch (activeSection.value) {
    case 'account':
      await saveAccount(); break
    case 'addresses':
      await saveAddresses(); break
    case 'leaves':
      await saveLeaves(); break
    case 'images':
      await saveImages(); break
    case 'description':
      await saveDescriptions(); break
  }
}

const handleSectionClick = async (target: string) => {
  if (activeSection.value === target) return
  // Plus de popup natif; on change simplement de section
  activeSection.value = target
  
  // Scroll vers le point d'ancrage en bas
  await nextTick()
  setTimeout(() => {
    const anchor = document.getElementById('general-info-anchor')
    if (anchor) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, 100)
}

// Extraction message d'erreur lisible
const extractErrorMessage = (e: unknown): string => {
  if (!e) return 'Échec de la sauvegarde (raison inconnue)'
  if (e instanceof Error) return e.message
  try { return JSON.stringify(e) } catch { return String(e) }
}

// Lifecycle
onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  loadVendeurData()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(PageHeader), {
      title: "Informations générales",
      subtitle: "Gérez vos informations personnelles et professionnelles"
    }),
    _createElementVNode("div", _hoisted_2, [
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, _cache[18] || (_cache[18] = [
            _createElementVNode("div", { class: "animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" }, null, -1),
            _createElementVNode("span", { class: "ml-3 text-black" }, "Chargement des données...", -1)
          ])))
        : (formData.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", {
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (handleSectionClick('account'))),
                  class: _normalizeClass([
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection.value === 'account'
              ? 'border-blue-500 bg-blue-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
          ])
                }, _cache[19] || (_cache[19] = [
                  _createElementVNode("div", { class: "text-center" }, [
                    _createElementVNode("div", { class: "w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3" }, [
                      _createElementVNode("i", { class: "fas fa-user text-blue-600 text-xl" })
                    ]),
                    _createElementVNode("h3", { class: "font-semibold text-black text-sm" }, "Paramètres du compte")
                  ], -1)
                ]), 2),
                _createElementVNode("div", {
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (handleSectionClick('addresses'))),
                  class: _normalizeClass([
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection.value === 'addresses'
              ? 'border-green-500 bg-green-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-md'
          ])
                }, _cache[20] || (_cache[20] = [
                  _createElementVNode("div", { class: "text-center" }, [
                    _createElementVNode("div", { class: "w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3" }, [
                      _createElementVNode("i", { class: "fas fa-map-marker-alt text-green-600 text-xl" })
                    ]),
                    _createElementVNode("h3", { class: "font-semibold text-black text-sm" }, "Adresses")
                  ], -1)
                ]), 2),
                _createElementVNode("div", {
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (handleSectionClick('leaves'))),
                  class: _normalizeClass([
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection.value === 'leaves'
              ? 'border-purple-500 bg-purple-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
          ])
                }, _cache[21] || (_cache[21] = [
                  _createElementVNode("div", { class: "text-center" }, [
                    _createElementVNode("div", { class: "w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3" }, [
                      _createElementVNode("i", { class: "fas fa-calendar-alt text-purple-600 text-xl" })
                    ]),
                    _createElementVNode("h3", { class: "font-semibold text-black text-sm" }, "Gestion des congés")
                  ], -1)
                ]), 2),
                _createElementVNode("div", {
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (handleSectionClick('images'))),
                  class: _normalizeClass([
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection.value === 'images'
              ? 'border-orange-500 bg-orange-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
          ])
                }, _cache[22] || (_cache[22] = [
                  _createElementVNode("div", { class: "text-center" }, [
                    _createElementVNode("div", { class: "w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3" }, [
                      _createElementVNode("i", { class: "fas fa-images text-orange-600 text-xl" })
                    ]),
                    _createElementVNode("h3", { class: "font-semibold text-black text-sm" }, "Images et médias")
                  ], -1)
                ]), 2),
                _createElementVNode("div", {
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (handleSectionClick('description'))),
                  class: _normalizeClass([
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection.value === 'description'
              ? 'border-cyan-500 bg-cyan-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-cyan-300 hover:shadow-md'
          ])
                }, _cache[23] || (_cache[23] = [
                  _createElementVNode("div", { class: "text-center" }, [
                    _createElementVNode("div", { class: "w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3" }, [
                      _createElementVNode("i", { class: "fas fa-globe text-cyan-600 text-xl" })
                    ]),
                    _createElementVNode("h3", { class: "font-semibold text-black text-sm" }, "Description multi-langues")
                  ], -1)
                ]), 2)
              ]),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("button", {
                  onClick: _cache[5] || (_cache[5] = ($event: any) => (scrollNav('left'))),
                  class: "absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 shadow-md rounded-full flex items-center justify-center text-black hover:text-black hover:bg-white transition-all"
                }, _cache[24] || (_cache[24] = [
                  _createElementVNode("i", { class: "fas fa-chevron-left text-sm" }, null, -1)
                ])),
                _createElementVNode("button", {
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (scrollNav('right'))),
                  class: "absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 shadow-md rounded-full flex items-center justify-center text-black hover:text-black hover:bg-white transition-all"
                }, _cache[25] || (_cache[25] = [
                  _createElementVNode("i", { class: "fas fa-chevron-right text-sm" }, null, -1)
                ])),
                _createElementVNode("div", {
                  ref_key: "mobileNavRef",
                  ref: mobileNavRef,
                  class: "flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide mx-10"
                }, [
                  _createElementVNode("div", {
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (handleSectionClick('account'))),
                    class: _normalizeClass([
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection.value === 'account'
              ? 'border-blue-500 bg-blue-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
          ])
                  }, _cache[26] || (_cache[26] = [
                    _createElementVNode("div", { class: "text-center" }, [
                      _createElementVNode("div", { class: "w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2" }, [
                        _createElementVNode("i", { class: "fas fa-user text-blue-600 text-base" })
                      ]),
                      _createElementVNode("h3", { class: "font-semibold text-black text-xs" }, "Paramètres du compte")
                    ], -1)
                  ]), 2),
                  _createElementVNode("div", {
                    onClick: _cache[8] || (_cache[8] = ($event: any) => (handleSectionClick('addresses'))),
                    class: _normalizeClass([
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection.value === 'addresses'
              ? 'border-green-500 bg-green-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-md'
          ])
                  }, _cache[27] || (_cache[27] = [
                    _createElementVNode("div", { class: "text-center" }, [
                      _createElementVNode("div", { class: "w-9 h-9 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2" }, [
                        _createElementVNode("i", { class: "fas fa-map-marker-alt text-green-600 text-base" })
                      ]),
                      _createElementVNode("h3", { class: "font-semibold text-black text-xs" }, "Adresses")
                    ], -1)
                  ]), 2),
                  _createElementVNode("div", {
                    onClick: _cache[9] || (_cache[9] = ($event: any) => (handleSectionClick('leaves'))),
                    class: _normalizeClass([
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection.value === 'leaves'
              ? 'border-purple-500 bg-purple-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
          ])
                  }, _cache[28] || (_cache[28] = [
                    _createElementVNode("div", { class: "text-center" }, [
                      _createElementVNode("div", { class: "w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2" }, [
                        _createElementVNode("i", { class: "fas fa-calendar-alt text-purple-600 text-base" })
                      ]),
                      _createElementVNode("h3", { class: "font-semibold text-black text-xs" }, "Gestion des congés")
                    ], -1)
                  ]), 2),
                  _createElementVNode("div", {
                    onClick: _cache[10] || (_cache[10] = ($event: any) => (handleSectionClick('images'))),
                    class: _normalizeClass([
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection.value === 'images'
              ? 'border-orange-500 bg-orange-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
          ])
                  }, _cache[29] || (_cache[29] = [
                    _createElementVNode("div", { class: "text-center" }, [
                      _createElementVNode("div", { class: "w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2" }, [
                        _createElementVNode("i", { class: "fas fa-images text-orange-600 text-base" })
                      ]),
                      _createElementVNode("h3", { class: "font-semibold text-black text-xs" }, "Images et médias")
                    ], -1)
                  ]), 2),
                  _createElementVNode("div", {
                    onClick: _cache[11] || (_cache[11] = ($event: any) => (handleSectionClick('description'))),
                    class: _normalizeClass([
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection.value === 'description'
              ? 'border-cyan-500 bg-cyan-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-cyan-300 hover:shadow-md'
          ])
                  }, _cache[30] || (_cache[30] = [
                    _createElementVNode("div", { class: "text-center" }, [
                      _createElementVNode("div", { class: "w-9 h-9 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-2" }, [
                        _createElementVNode("i", { class: "fas fa-globe text-cyan-600 text-base" })
                      ]),
                      _createElementVNode("h3", { class: "font-semibold text-black text-xs" }, "Description multi-langues")
                    ], -1)
                  ]), 2)
                ], 512)
              ]),
              _cache[31] || (_cache[31] = _createElementVNode("div", {
                id: "general-info-anchor",
                class: "scroll-mt-4"
              }, null, -1)),
              _cache[32] || (_cache[32] = _createElementVNode("div", { class: "mb-4" }, [
                _createElementVNode("div", { class: "alert-save mb-2" }, [
                  _createElementVNode("i", { class: "fas fa-info-circle" }),
                  _createElementVNode("p", null, [
                    _createElementVNode("strong", null, "N'oubliez pas :"),
                    _createTextVNode(" Appuyez sur \"Sauvegarder\" pour enregistrer vos modifications. ")
                  ])
                ])
              ], -1)),
              _createElementVNode("div", _hoisted_7, [
                _withDirectives(_createElementVNode("div", null, [
                  _createVNode(_unref(AccountSettingsSection), {
                    "form-data": formData.value,
                    saving: isSaving.value,
                    "disable-save": hasAccountErrors.value,
                    "error-message": accountError.value,
                    onSave: saveAccount,
                    "onUpdate:formData": _cache[12] || (_cache[12] = ($event: any) => (formData.value = $event)),
                    onValidationChanged: handleValidationChanged
                  }, null, 8, ["form-data", "saving", "disable-save", "error-message"])
                ], 512), [
                  [_vShow, activeSection.value === 'account']
                ]),
                _withDirectives(_createElementVNode("div", null, [
                  _createVNode(_unref(AddressesSection), {
                    "form-data": formData.value,
                    saving: isSaving.value,
                    "disable-save": hasAddressErrors.value,
                    onSave: saveAddresses,
                    "onUpdate:formData": _cache[13] || (_cache[13] = ($event: any) => (formData.value = $event)),
                    onValidationChanged: handleAddressValidationChanged
                  }, null, 8, ["form-data", "saving", "disable-save"])
                ], 512), [
                  [_vShow, activeSection.value === 'addresses']
                ]),
                _withDirectives(_createElementVNode("div", null, [
                  _createVNode(_unref(LeavesSection), {
                    "form-data": formData.value,
                    saving: isSaving.value,
                    "disable-save": false,
                    onSave: saveLeaves,
                    onAddLeave: saveLeavesWith,
                    "onUpdate:formData": _cache[14] || (_cache[14] = ($event: any) => (formData.value = $event))
                  }, null, 8, ["form-data", "saving"])
                ], 512), [
                  [_vShow, activeSection.value === 'leaves']
                ]),
                _withDirectives(_createElementVNode("div", null, [
                  _createVNode(_unref(ImagesSection), {
                    ref_key: "imagesSectionRef",
                    ref: imagesSectionRef,
                    "image-urls": imageUrls.value,
                    saving: isSaving.value,
                    "disable-save": false,
                    onSave: saveImages,
                    onImagesChanged: handleImagesChanged,
                    onDeleteImage: handleDeleteImage
                  }, null, 8, ["image-urls", "saving"])
                ], 512), [
                  [_vShow, activeSection.value === 'images']
                ]),
                _withDirectives(_createElementVNode("div", null, [
                  _createVNode(_unref(DescriptionSection), {
                    "form-data": formData.value,
                    saving: isSaving.value,
                    "disable-save": false,
                    onSave: saveDescriptions,
                    "onUpdate:formData": _cache[15] || (_cache[15] = ($event: any) => (formData.value = $event))
                  }, null, 8, ["form-data", "saving"])
                ], 512), [
                  [_vShow, activeSection.value === 'description']
                ])
              ])
            ]))
          : _createCommentVNode("", true)
    ]),
    _createVNode(_unref(ConfirmModal), {
      show: showValidationModal.value,
      title: "Champs obligatoires manquants",
      message: "Veuillez remplir tous les champs obligatoires (étoile rouge).",
      "confirm-text": "OK",
      onConfirm: _cache[16] || (_cache[16] = ($event: any) => (showValidationModal.value = false)),
      onCancel: _cache[17] || (_cache[17] = ($event: any) => (showValidationModal.value = false))
    }, null, 8, ["show"])
  ]))
}
}

})